/*
 * TerriSTORY®
 *
 © Copyright 2022 AURA-EE
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * A copy of the GNU Affero General Public License should be present along
 * with this program at the root of current repository. If not, see
 * http://www.gnu.org/licenses/.
 */

import React from "react";
import { buildRegionUrl, createPdfMethodoLink, slugify } from "../utils.js";
import jszip from "jszip";
import saveAs from "file-saver";
import config from "../settings.js";

/**
 * Allow to export general data using an external callback when send button is clicked.
 *
 * Use ExportForm so the user can select options (include or not PDF is any PDF is
 * linked, select format between CSV or Excel readable - ie. with right encoding).
 *
 * @prop parentApi
 * @prop {callable} exportFunction The function called when pushing export button
 * @prop {boolean} methodoPdf whether a PDF file is associated with current data
 */
export class GenericExportButton extends React.Component {
  constructor(props) {
    super(props);
    this.openModal = () => {
      this.setState({
        isModalOpen: true
      });
    };
    this.closeModal = () => {
      this.setState({
        isModalOpen: false
      });
    };
    this.onExport = (format, exportYears, includePDF, resolve) => {
      this.props.exportFunction(format, includePDF, () => {
        this.closeModal();
        resolve();
      });
    };
    this.state = {
      isModalOpen: false
    };
  }
  render() {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territorialsynthesis-methodo btn btn-primary",
      "data-dismiss": "alert",
      onClick: this.openModal
    }, "Exporter les donn\xE9es"), this.state.isModalOpen && /*#__PURE__*/React.createElement(ExportForm, {
      parentApi: this.props.parentApi,
      closeModal: this.closeModal,
      methodoPdf: this.props.methodoPdf,
      analysisId: false,
      analysisSelectedYear: false,
      exportYears: false,
      onExport: this.onExport,
      disableGIS: true
    }));
  }
}

/**
 * Allow to export an indicator data using API link.
 *
 * Use ExportForm so the user can select options (include current year if provided or all years,
 * include or not PDF is any PDF is linked, select format between CSV or Excel readable - ie. with right encoding).
 *
 * @prop parentApi
 * @prop {number} analysisId
 * @prop {number?} analysisSelectedYear L'année actuelle séléctionnée
 * @prop {string?} exportYears Si spécifié choisi les années à exporter à la place de l'utilisateur
 */
export class ExportIndicatorButton extends React.Component {
  constructor(props) {
    super(props);
    this.openModal = () => {
      this.setState({
        isModalOpen: true
      });
    };
    this.closeModal = () => {
      this.setState({
        isModalOpen: false
      });
    };
    this.onExport = async (format, exportYears, includePDF, resolve) => {
      var _this$props$analysisI, _this$props$zoneType, _this$props, _this$props$zoneId, _this$props2, _this$props$zoneMaill, _this$props3, _this$props$analysisS;
      const analysisId = (_this$props$analysisI = this.props.analysisId) !== null && _this$props$analysisI !== void 0 ? _this$props$analysisI : this.props.parentApi.data.analysis;
      const analysisManager = this.props.parentApi.controller.analysisManager;
      const zoneType = (_this$props$zoneType = (_this$props = this.props) === null || _this$props === void 0 ? void 0 : _this$props.zoneType) !== null && _this$props$zoneType !== void 0 ? _this$props$zoneType : this.props.parentApi.data.zone.zone;
      const zoneId = (_this$props$zoneId = (_this$props2 = this.props) === null || _this$props2 === void 0 ? void 0 : _this$props2.zoneId) !== null && _this$props$zoneId !== void 0 ? _this$props$zoneId : this.props.parentApi.data.currentZone;
      const zoneMaille = (_this$props$zoneMaill = (_this$props3 = this.props) === null || _this$props3 === void 0 ? void 0 : _this$props3.zoneMaille) !== null && _this$props$zoneMaill !== void 0 ? _this$props$zoneMaill : this.props.parentApi.data.zone.maille;
      let exportUrl = buildRegionUrl(config.api_analysis_export, this.props.parentApi.data.region).replace("#id#", analysisId) + `?zone=${zoneType}` + `&zone_id=${zoneId}` + `&format=${format}`;
      const pdfName = analysisManager.getAnalysisMethodoPdf(analysisId);
      let pdfUrl;
      if (includePDF) pdfUrl = createPdfMethodoLink(config.methodo_url, this.props.parentApi.data.region, pdfName);
      let exportedFilename = analysisManager.getAnalysisName(analysisId);
      exportedFilename = slugify(exportedFilename);
      exportedFilename += format === "excel" ? ".xlsx" : format === "gpkg" ? ".gpkg" : ".csv";
      let mailles = [zoneType, zoneMaille];
      if (zoneType === zoneMaille) {
        mailles = [zoneType];
      }
      let years = [exportYears === "all" ? "" : (_this$props$analysisS = this.props.analysisSelectedYear) !== null && _this$props$analysisS !== void 0 ? _this$props$analysisS : analysisManager.getAnalysisLastYear(analysisId)];
      if (exportYears === "all" && ["gpkg"].includes(format)) {
        years = analysisManager.getAnalysisYears(analysisId);
      }
      const exportAttr = mailles.map(maille => years.map(year => ({
        maille,
        year
      }))).flat();
      try {
        const buffers = await Promise.all(exportAttr.map(_ref => {
          let {
            maille,
            year
          } = _ref;
          return {
            maille: `&maille=${maille}`,
            year: year ? `&annee=${year}` : ""
          };
        }).map(async _ref2 => {
          let {
            maille,
            year
          } = _ref2;
          const response = await fetch(exportUrl + maille + year);
          if (!response.ok) {
            const e = await response.json();
            console.error("Error during export: ", e["message"]);
            return null;
          }
          return response.arrayBuffer();
        }));
        const zip = new jszip();
        for (let i in buffers) {
          if (buffers[i] != null) {
            zip.file((mailles.length > 1 ? `${exportAttr[i].maille}_` : "") + (years.length > 1 ? `${exportAttr[i].year}_` : "") + exportedFilename, buffers[i]);
          }
        }
        if (pdfUrl) {
          const PdfMethodo = fetch(pdfUrl).then(t => t.blob());
          zip.file(pdfName, PdfMethodo);
        }
        const blob = await zip.generateAsync({
          type: "blob"
        });
        exportedFilename = exportedFilename.split(".")[0] + ".zip";
        saveAs(blob, exportedFilename);
      } catch (e) {
        alert(e);
      } finally {
        this.closeModal();
        resolve();
      }
    };
    this.state = {
      isModalOpen: false
    };
  }
  render() {
    var _this$props$analysisI2;
    let analysisId = (_this$props$analysisI2 = this.props.analysisId) !== null && _this$props$analysisI2 !== void 0 ? _this$props$analysisI2 : this.props.parentApi.data.analysis;
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("button", {
      "aria-hidden": "true",
      style: {
        border: "none",
        padding: "1px"
      },
      title: "Exporter " + this.props.parentApi.controller.analysisManager.getAnalysisName(analysisId),
      className: "bi-download " + this.props.className,
      onClick: this.openModal
    }), this.state.isModalOpen && /*#__PURE__*/React.createElement(ExportForm, {
      parentApi: this.props.parentApi,
      analysisSelectedYear: this.props.analysisSelectedYear,
      closeModal: this.closeModal,
      analysisId: analysisId,
      exportYears: this.props.exportYears,
      onExport: this.onExport
    }));
  }
}

/**
 * Show a modal so the user can select options related to an export.
 *
 * Options provided go as follow:
 *   - include current year if provided or all years,
 *   - include or not PDF is any PDF is linked (thus creating a zip),
 *   - select format between CSV or Excel readable - ie. with right encoding.
 *
 * Require a callback function that is called when button is pressed.
 *
 * @prop parentApi
 * @prop {callable} onExport Called when submit button pressed
 * @prop {number} analysisId
 * @prop {number?} analysisSelectedYear Current selected year
 * @prop {string?} exportYears If specified, user won't have the choice on years
 * @prop {boolean} methodoPdf whether a PDF file is associated with current data
 */
class ExportForm extends React.Component {
  constructor(props) {
    var _props$exportYears;
    super(props);
    this.onIncludePDFChange = event => {
      this.setState({
        includePDF: event.target.checked
      });
    };
    this.onYearsChange = event => {
      this.setState({
        exportYears: event.target.value
      });
    };
    this.onFormatChange = event => {
      this.setState({
        format: event.target.value
      });
    };
    this.onExport = () => {
      this.setState({
        disableExport: true
      });
      this.props.onExport(this.state.format, this.state.exportYears, this.state.includePDF, () => {
        this.setState({
          disableExport: false
        });
      });
    };
    this.state = {
      disableExport: false,
      includePDF: false,
      exportYears: (_props$exportYears = props.exportYears) !== null && _props$exportYears !== void 0 ? _props$exportYears : "current",
      format: "excel"
    };
  }
  handleClick(e) {
    // do not handle click when clicking on inner div and not on main div
    if (e.target !== e.currentTarget) {
      return;
    }
    this.props.closeModal();
  }
  render() {
    var _this$props$analysisI3, _this$props$analysisS2, _this$props$methodoPd;
    const analysisId = (_this$props$analysisI3 = this.props.analysisId) !== null && _this$props$analysisI3 !== void 0 ? _this$props$analysisI3 : this.props.parentApi.data.analysis;
    let year = (_this$props$analysisS2 = this.props.analysisSelectedYear) !== null && _this$props$analysisS2 !== void 0 ? _this$props$analysisS2 : this.props.parentApi.controller.analysisManager.getAnalysisLastYear(analysisId);
    const methodoPdf = (_this$props$methodoPd = this.props.methodoPdf) !== null && _this$props$methodoPd !== void 0 ? _this$props$methodoPd : this.props.parentApi.controller.analysisManager.getAnalysisMethodoPdf(analysisId);
    const hasMethodoPdf = methodoPdf && methodoPdf.endsWith(".pdf") && !methodoPdf.startsWith("http");
    const years = this.props.parentApi.controller.analysisManager.getAnalysisYears(analysisId);
    const hasSeveralYears = years && Array.isArray(years) && years.length > 1;
    return /*#__PURE__*/React.createElement("div", {
      className: "modal",
      onClick: e => this.handleClick(e)
    }, /*#__PURE__*/React.createElement("div", {
      className: "modal-content"
    }, /*#__PURE__*/React.createElement("span", {
      className: "close",
      onClick: e => this.handleClick(e)
    }, "\xD7"), /*#__PURE__*/React.createElement("div", null, hasSeveralYears && !this.props.exportYears && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
      style: {
        clear: "both"
      }
    }, "S\xE9lectionner les ann\xE9es \xE0 exporter :"), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("input", {
      type: "radio",
      name: "years",
      value: "current",
      id: "export-years-current",
      checked: this.state.exportYears === "current",
      onChange: this.onYearsChange
    }), " ", /*#__PURE__*/React.createElement("label", {
      htmlFor: "export-years-current"
    }, year)), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("input", {
      type: "radio",
      name: "years",
      value: "all",
      id: "export-years-all",
      checked: this.state.exportYears === "all",
      onChange: this.onYearsChange
    }), " ", /*#__PURE__*/React.createElement("label", {
      htmlFor: "export-years-all"
    }, "Toutes")), /*#__PURE__*/React.createElement("br", null)), /*#__PURE__*/React.createElement("p", null, "S\xE9lectionner le format du fichier de donn\xE9es :"), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("input", {
      type: "radio",
      name: "format",
      value: "excel",
      id: "export-format-excel",
      checked: this.state.format === "excel",
      onChange: this.onFormatChange
    }), " ", /*#__PURE__*/React.createElement("label", {
      htmlFor: "export-format-excel"
    }, "Excel")), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("input", {
      type: "radio",
      name: "format",
      value: "csv",
      id: "export-format-csv",
      checked: this.state.format === "csv",
      onChange: this.onFormatChange
    }), " ", /*#__PURE__*/React.createElement("label", {
      htmlFor: "export-format-csv"
    }, "CSV")), !this.props.disableGIS && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("input", {
      type: "radio",
      name: "format",
      value: "gpkg",
      id: "export-format-gpkg",
      checked: this.state.format === "gpkg",
      onChange: this.onFormatChange
    }), " ", /*#__PURE__*/React.createElement("label", {
      htmlFor: "export-format-gpkg"
    }, "GeoPackage")), /*#__PURE__*/React.createElement("br", null), hasMethodoPdf && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      id: "export-include-pdf",
      checked: this.state.includePDF,
      onChange: this.onIncludePDFChange
    }), " ", /*#__PURE__*/React.createElement("label", {
      htmlFor: "export-include-pdf"
    }, "Inclure le PDF m\xE9thodologique")), /*#__PURE__*/React.createElement("br", null)), this.state.format === "gpkg" && this.state.exportYears === "all" && /*#__PURE__*/React.createElement("p", null, "Attention : le temps d\u2019export peut \xEAtre long"), /*#__PURE__*/React.createElement("center", null, /*#__PURE__*/React.createElement("button", {
      className: "btn btn-primary",
      onClick: this.onExport,
      disabled: this.state.disableExport
    }, this.state.disableExport && /*#__PURE__*/React.createElement("span", {
      className: "spinner-border spinner-border-sm me-1",
      role: "status",
      "aria-hidden": "true"
    }), "Exporter")))));
  }
}